/**
 * The REALISM PANEL, the one shell screen the game ships a resource for.
 *
 * `SIMREAL.BOX` (35 int16 rects) was validated rect-by-rect against
 * `realism-panel.png` by the ui-reference lane; this lane recovered the offset
 * between the two — **BOX (x, y) sits at screen (x + 9, y + 30)**, and a BOX
 * rect describes the *orange* plate or the checkbox's black interior, not the
 * tan surround. Every number below is either that arithmetic applied to a
 * documented rect or a direct measurement off the capture, and the two agree:
 * BOX's three `84×12` presets at y=6 land on the measured tan buttons at
 * (19,33), (115,33), (211,33), 90×18 each.
 *
 * Eleven checkboxes, not twelve: the right column's sixth slot is the
 * `COMBAT LEVEL` selector, which is why the file has eleven `7×7` rects.
 */

import {
  overallDifficulty,
  REALISM_LEFT_COLUMN,
  REALISM_TOGGLE_IDS,
  REALISM_TOGGLE_LABELS,
  toggleValue,
  type RealismPanel,
} from "../../mission/realism-settings.ts";
import { FLIGHT_MODEL_TIERS } from "@red-opus/engine";
import type { Rect } from "../render/draw-ops.ts";
import type { MenuAction } from "./menu-model.ts";
import type { ShellScreen, Widget } from "./shell-screen.ts";

/** BOX-space → screen-space. */
export function fromBox(x: number, y: number, w: number, h: number): Rect {
  return { x: x + 9, y: y + 30, w, h };
}

/** A BOX rect names the orange plate; the tan button around it is three pixels larger on every side. */
function buttonFromBox(x: number, y: number, w: number, h: number): Rect {
  const inner = fromBox(x, y, w, h);
  return { x: inner.x - 3, y: inner.y - 3, w: inner.w + 6, h: inner.h + 6 };
}

export const PRESET_RECTS: readonly Rect[] = [13, 109, 205].map((x) => buttonFromBox(x, 6, 84, 12));
export const RESTORE_RECT = buttonFromBox(13, 139, 84, 12);
export const ACCEPT_RECT = buttonFromBox(205, 139, 84, 12);

/** The 7×7 interiors from `SIMREAL.BOX`; the sunken frame around each is two pixels larger. */
const CHECK_LADDER = [30, 45, 60, 75, 90, 105];

function checkboxRect(column: 0 | 1, row: number): Rect {
  const interior = fromBox(column === 0 ? 14 : 156, CHECK_LADDER[row] ?? 30, 7, 7);
  return { x: interior.x - 2, y: interior.y - 2, w: 11, h: 11 };
}

/** Measured off the capture: the label chips run 34..156 on the left and 176..298 on the right, 13 rows tall, one row above their checkbox. */
function labelPlateRect(column: 0 | 1, row: number): Rect {
  return { x: column === 0 ? 34 : 176, y: checkboxRect(column, row).y - 1, w: 123, h: 13 };
}

export const COMBAT_LABEL_RECT: Rect = { x: 163, y: 132, w: 77, h: 13 };
export const COMBAT_VALUE_RECT: Rect = { x: 242, y: 133, w: 56, h: 11 };
export const FLIGHT_MODEL_LABEL_RECT: Rect = { x: 80, y: 147, w: 77, h: 13 };
export const FLIGHT_MODEL_VALUE_RECT: Rect = { x: 163, y: 148, w: 79, h: 11 };
/** The readout's own framed box, inside the notched group box the capture draws around it. */
export const DIFFICULTY_GROUP_RECT: Rect = { x: 111, y: 160, w: 98, h: 24 };
export const DIFFICULTY_VALUE_RECT: Rect = { x: 146, y: 169, w: 29, h: 11 };

export const REALISM_OUTER_PLATE: Rect = { x: 6, y: 27, w: 307, h: 163 };
export const REALISM_INNER_PLATE: Rect = { x: 13, y: 52, w: 294, h: 113 };

const command = (name: string): MenuAction => ({ kind: "command", command: name });

/**
 * A value inside a black well: FONT3 in flat `248`, no emboss, centred by the
 * same rule the chips use. Measured on `EASY`, `NOVICE` and the difficulty
 * readout.
 */
function wellValue(rect: Rect, text: string): Widget {
  return { kind: "wellText", rect, text };
}

function toggleWidgets(panel: RealismPanel, locked: boolean): Widget[] {
  return REALISM_TOGGLE_IDS.map((id, index): Widget => {
    const column = index < REALISM_LEFT_COLUMN ? 0 : 1;
    const row = column === 0 ? index : index - REALISM_LEFT_COLUMN;
    return {
      kind: "toggle",
      id,
      box: checkboxRect(column, row),
      plate: labelPlateRect(column, row),
      label: REALISM_TOGGLE_LABELS[id],
      checked: toggleValue(panel, id),
      enabled: !locked || LIVE_IN_FLIGHT.has(id),
      action: command(`toggle:${id}`),
    };
  });
}

/**
 * In flight the original locks everything structural: only `SUN BLIND SPOT`,
 * `REALISTIC WEATHER`, `BLACKOUTS ALLOWED`, `REAL NAVIGATION`, `COMBAT LEVEL`,
 * `RESTORE` and `ACCEPT` stay live (`ui-reference/index.md`). Nothing in this
 * port reaches the panel mid-flight yet, but the rule is encoded rather than
 * left to be rediscovered.
 */
const LIVE_IN_FLIGHT: ReadonlySet<string> = new Set(["sunBlindSpot", "realisticWeather", "blackouts", "realNavigation"]);

export interface RealismScreenOptions {
  readonly panel: RealismPanel;
  /** `REALISM PANEL` before a mission, `REALISM MENU` in flight — same screen, different string. */
  readonly inFlight?: boolean;
}

export function realismScreen(options: RealismScreenOptions): ShellScreen {
  const { panel } = options;
  const locked = options.inFlight === true;
  const presets: Widget[] = FLIGHT_MODEL_TIERS.map((tier, index) => ({
    kind: "button",
    id: `preset-${tier}`,
    rect: PRESET_RECTS[index] ?? PRESET_RECTS[0]!,
    label: tier.toUpperCase(),
    enabled: !locked,
    action: command(`preset:${tier}`),
  }));
  return {
    id: "realism",
    chrome: { kind: "panel", title: locked ? "REALISM MENU" : "REALISM PANEL", emblem: "none", background: undefined },
    widgets: [
      { kind: "plate", rect: REALISM_OUTER_PLATE },
      ...presets,
      { kind: "plate", rect: REALISM_INNER_PLATE },
      ...toggleWidgets(panel, locked),
      { kind: "value", id: "combat-level", rect: COMBAT_LABEL_RECT, text: "COMBAT LEVEL", enabled: !locked, action: command("combat") },
      { kind: "well", rect: COMBAT_VALUE_RECT },
      wellValue(COMBAT_VALUE_RECT, panel.combatLevel),
      { kind: "value", id: "flight-model", rect: FLIGHT_MODEL_LABEL_RECT, text: "FLIGHT MODEL", enabled: !locked, action: command("flight-model") },
      { kind: "well", rect: FLIGHT_MODEL_VALUE_RECT },
      wellValue(FLIGHT_MODEL_VALUE_RECT, panel.tier.toUpperCase()),
      { kind: "plate", rect: DIFFICULTY_GROUP_RECT },
      { kind: "text", text: "OVERALL DIFFICULTY", x: 116, y: 164, role: "label" },
      { kind: "well", rect: DIFFICULTY_VALUE_RECT },
      wellValue(DIFFICULTY_VALUE_RECT, String(overallDifficulty(panel))),
      {
        kind: "button",
        id: "restore",
        rect: RESTORE_RECT,
        label: "RESTORE",
        enabled: true,
        action: command("restore"),
      },
      { kind: "button", id: "accept", rect: ACCEPT_RECT, label: "ACCEPT", enabled: true, action: command("realism-accept") },
    ],
    cancel: command("realism-accept"),
  };
}
